// ---------------------------------------------------------------------------
// AutoMate — feature flags and static resources
// ---------------------------------------------------------------------------

/**
 * Master switch for the AutoMate AI Search feature.
 *
 * When false, the AISearchBar, SearchPopup, and AutoMateBubble are all hidden.
 * Flip to true once the agent backend is wired up for the current environment.
 */
export const AI_SEARCH_ENABLED = true;

/**
 * When true, the ChatPanel displays the time-to-first-token and
 * time-to-last-token for each assistant response, and both values are
 * logged to the console. Useful while iterating on the agent's latency.
 *
 * Flip to false to hide the timing chips and silence the console logs.
 */
export const AI_SEARCH_SHOW_TIMING = false;

/**
 * When true, `AISearchAppSync.js` emits verbose WebSocket and mutation logs
 * (endpoint URLs, raw chunk payloads, AppSync protocol messages) to the
 * browser console. Useful while debugging the agent pipeline locally.
 *
 * Leave false in production/beta — the logs include user questions and
 * AI-generated responses and should not reach the browser console there.
 * Errors and unexpected transport states are always logged regardless.
 */
export const AI_SEARCH_DEBUG_LOGS = false;

// ---------------------------------------------------------------------------
// Timeout thresholds for the agent response
//
// The agent occasionally takes a while to produce its first token (complex
// queries, orchestrator cold starts) or can stall mid-stream. These thresholds
// drive the soft / hard timeouts in `useAgentChat`:
//
//   - At `FIRST_TOKEN_WARN_MS` with no chunks yet → show a "still waiting"
//     banner but keep the subscription open; if a chunk arrives, the banner
//     is cleared and streaming continues normally.
//   - At `INTER_CHUNK_WARN_MS` between consecutive chunks → same soft banner.
//   - At `HARD_TIMEOUT_MS` total elapsed → give up: unsubscribe, replace the
//     assistant placeholder with an error message, and re-enable the input.
//
// Pick values comfortably above observed p99 latency so we don't cry wolf.
// ---------------------------------------------------------------------------

/** Show a "still waiting" banner if no first token arrives after this many ms. */
export const AI_SEARCH_FIRST_TOKEN_WARN_MS = 30_000;

/** Show a "still waiting" banner if no new chunk arrives for this many ms mid-stream. */
export const AI_SEARCH_INTER_CHUNK_WARN_MS = 20_000;

/** Hard ceiling — after this many ms total we stop waiting and show an error. */
export const AI_SEARCH_HARD_TIMEOUT_MS = 120_000;

/**
 * Hard-coded links surfaced in the left-hand results panel of SearchPopup.
 * These act as placeholder search results until the agent-driven results API
 * is in place. The inner keys are the deployment stage so we can point at the
 * right environment per build.
 *
 * TODO: Remove once the backend returns real search results dynamically.
 */
export const AUTOMATE_SEARCH_LINKS = {
    ACCOUNTING_CENTRAL_APP: {
        "dev" : "https://beta.bi-central.accounting.amazon.dev/App/View=details",
        "beta" : "https://beta.bi-central.accounting.amazon.dev/App/View=details",
        "prod" : "https://bi-central.accounting.amazon.dev/App/View=details"
    },
    CLOSE_PORTAL_REPORTING: {
        "dev" : "https://beta.bi-central.accounting.amazon.dev/ClosePortal/REPORTING/Page/2",
        "beta" : "https://beta.bi-central.accounting.amazon.dev/ClosePortal/REPORTING/Page/2",
        "prod" : "https://bi-central.accounting.amazon.dev/ClosePortal/REPORTING/Page/2"
    },
    CLOSE_PORTAL_OTHER_INFO: {
        "dev" : "https://beta.bi-central.accounting.amazon.dev/ClosePortal/Other%20Information/Page/7",
        "beta" : "https://beta.bi-central.accounting.amazon.dev/ClosePortal/Other%20Information/Page/7",
        "prod" : "https://bi-central.accounting.amazon.dev/ClosePortal/Other%20Information/Page/7"
    },
    CLOSE_PORTAL_HOME_PAGE: {
        "dev" : "https://beta.bi-central.accounting.amazon.dev/ClosePortal/HOME/Page/24",
        "beta" : "https://beta.bi-central.accounting.amazon.dev/ClosePortal/HOME/Page/24",
        "prod" : "https://bi-central.accounting.amazon.dev/ClosePortal/HOME/Page/24"
    },
}
